import { useEffect } from 'react'
import {
  addCrossClientLandingFinishedExecutionProgresses,
  addCrossClientLandingFinishedExecutions,
  resetCrossClientLanding,
  selectCrossClientLandingFinishedExecutionProgresses,
  selectCrossClientLandingFinishedExecutions,
  setCrossClientLandingFinishedExecutionsLoading,
  setCrossClientLandingRunningExecutions,
  setCrossClientLandingRunningExecutionsLoading,
  setCrossClientLandingTeammates,
  setCrossClientLandingTeammatesLoading,
  setCrossClientLandingTotalFinishedExecutions,
} from '../../../store/crossClient/crossClientLanding'
import {
  useLazyGetAllCrossClientQueryExecutionsQuery,
  useLazyGetCrossClientQueryExecutionProgressQuery,
  useLazyGetCrossClientQueryExecutionsQuery,
} from '../../../store/crossClient/crossClient'
import { useAppDispatch, useAppSelector } from '../../../store/hooks'
import { selectAccountId } from '../../../store/auth/authSlice'
import { useLazyGetAllUsersQuery } from '../../../store/user/user'
import { CROSS_EXECUTION_CLIENT_FINISHED_PHASES, CROSS_EXECUTION_CLIENT_RUNNING_PHASES } from '../../../store/crossClient/utils'

const PAGE_SIZE = 5

export const CrossClientLandingInitialization = () => {
  const dispatch = useAppDispatch()

  const accountId = useAppSelector(selectAccountId)
  const finishedExecutions = useAppSelector(selectCrossClientLandingFinishedExecutions)
  const finishedExecutionProgresses = useAppSelector(selectCrossClientLandingFinishedExecutionProgresses)

  const [triggerGetCrossClientExecutions] = useLazyGetCrossClientQueryExecutionsQuery()
  const [triggerGetAllCrossClientExecutions] = useLazyGetAllCrossClientQueryExecutionsQuery()
  const [triggerGetCrossClientExecutionProgress] = useLazyGetCrossClientQueryExecutionProgressQuery()
  const [triggerGetAllUsers] = useLazyGetAllUsersQuery()

  useEffect(() => {
    const getFinishedExecutions = async () => {
      try {
        dispatch(setCrossClientLandingFinishedExecutionsLoading(true))
        const executions = await triggerGetCrossClientExecutions({
          accountId,
          phases: [...CROSS_EXECUTION_CLIENT_FINISHED_PHASES],
          pageRequest: { page: 0, size: PAGE_SIZE },
        }).unwrap()
        if (!executions.data.length) {
          return
        }
        dispatch(addCrossClientLandingFinishedExecutions(executions.data))
        dispatch(setCrossClientLandingTotalFinishedExecutions(executions.page.totalElements))
      } catch (e) {
        console.error(e)
      } finally {
        dispatch(setCrossClientLandingFinishedExecutionsLoading(false))
      }
    }
    getFinishedExecutions()
  }, [dispatch, accountId, triggerGetCrossClientExecutions])

  useEffect(() => {
    const getCrossClientLandingTeammates = async () => {
      try {
        const teammates = await triggerGetAllUsers({ accountId, identityTypes: ['idp'] }).unwrap()
        if (teammates?.data) {
          dispatch(setCrossClientLandingTeammates(teammates.data))
        }
      } finally {
        dispatch(setCrossClientLandingTeammatesLoading(false))
      }
    }
    getCrossClientLandingTeammates()
  }, [dispatch, accountId, triggerGetAllUsers])

  useEffect(() => {
    if (!finishedExecutions.length) {
      return
    }
    const missingProgressExecutionIds = finishedExecutions
      .map((e) => e.id)
      .filter((executionId) => !finishedExecutionProgresses.some((p) => p.executionId === executionId))
    if (!missingProgressExecutionIds.length) {
      return
    }
    const getMissingFinishedExecutionProgress = async () => {
      try {
        const progresses = []
        for (const executionId of missingProgressExecutionIds) {
          const progress = await triggerGetCrossClientExecutionProgress({ accountId, executionId }).unwrap()
          if (progress) {
            progresses.push(progress)
          }
        }
        if (progresses.length) {
          dispatch(addCrossClientLandingFinishedExecutionProgresses(progresses))
        }
      } catch (e) {
        console.error(e)
      }
    }
    getMissingFinishedExecutionProgress()
  }, [dispatch, accountId, finishedExecutions, finishedExecutionProgresses, triggerGetCrossClientExecutionProgress])

  useEffect(() => {
    const getRunningExecutions = async () => {
      try {
        dispatch(setCrossClientLandingRunningExecutionsLoading(true))
        const executions = await triggerGetAllCrossClientExecutions({
          accountId,
          phases: [...CROSS_EXECUTION_CLIENT_RUNNING_PHASES],
        }).unwrap()
        if (!executions.data.length) {
          return
        }
        dispatch(setCrossClientLandingRunningExecutions(executions.data))
      } catch (e) {
        console.error(e)
      } finally {
        dispatch(setCrossClientLandingRunningExecutionsLoading(false))
      }
    }
    getRunningExecutions()
  }, [dispatch, accountId, triggerGetAllCrossClientExecutions])

  useEffect(() => {
    dispatch(resetCrossClientLanding())
  }, [dispatch])

  return null
}
